import {
  ActivityIndicator,
  FlatList,
  Pressable,
  Text,
  View,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { EquipmentKind, MuscleRegion } from '@heraion/shared';

import { resolveTheme } from '../theme';
import { useThemePreferenceContext } from '../ThemeContext';
import { EmptyState } from '../primitives/EmptyState';
import { PickerSessionBand } from '../primitives/PickerSessionBand';
import { FilterChipsRow } from './FilterChipsRow';
import { ExercisePickerRow } from './ExercisePickerRow';
import { PickerFooter } from './PickerFooter';
import { usePickerDays } from './usePickerDays';
import { PickerSearchRow } from './PickerSearchRow';
import { PickerTabBar } from './PickerTabBar';
import { PickerPlanList } from './PickerPlanList';
import { PickerProgramList } from './PickerProgramList';
import { SupersetPanel } from './SupersetPanel';
import { makeStyles } from './pickerStyles';
import type { ExercisePickerProps } from './pickerProps';
import {
  useExercisePickerState,
  type PickerTab,
} from './useExercisePickerState';

export type { GroupOption, PairEntry, SupersetContext } from './SupersetPanel';
// ⛔ Ri-esportati, non ri-dichiarati: il contratto vive in `pickerProps.ts` e i
// chiamanti storici (il barrel, `hostDeps`) continuano a leggerlo da qui.
export type { ExercisePickerProps, PickerBookmarks } from './pickerProps';

// Unified Add-Exercise picker (inbox 963ca6f2). Two tabs — Esercizi / Superset
// — share ONE multi-select exercise list (recency-ordered, favourites pinned
// on top). The Superset tab layers a panel above the list (current exercise,
// join existing, pair in-workout, saved templates) and its footer creates a
// group in one shot. Opening from a card's ＋ defaults to the Superset tab.
//
// Shared component (P2 of the scheduler-composer plan): this is the picker
// CONTENT — the host owns the presentation shell (mobile wraps it in a
// pageSheet Modal + SafeAreaView; web-rnw mounts it in its own modal) and
// must render it inside a flex:1 column. Platform concerns arrive as props:
// the catalog source (cache-first, offline-safe), the bookmarks
// store, saved superset templates, the recency loader, the locale resolver
// and the pain-risk dot.

export function ExercisePicker({
  visible,
  onClose,
  onPickSingle,
  onAddMany,
  addDays,
  onCreateSuperset,
  onJoinGroup,
  supersetContext = null,
  presentSlugs = [],
  presentTreatment = 'hide',
  eligibleSlugs,
  dark,
  catalog,
  loadRecency,
  bookmarks,
  templates,
  confirm,
  onRemoveTemplate,
  getExerciseName,
  PainDot,
  usePainRiskLabel,
  gymChip,
  workouts,
  onAddWorkout,
  workoutImportWeeks,
  programs,
  onAddProgramDay,
}: ExercisePickerProps) {
  const singleMode = onPickSingle !== undefined;
  const family = useThemePreferenceContext()?.preference.family ?? 'brand';
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();

  const {
    tab,
    setTab,
    query,
    setQuery,
    regions,
    equipment,
    toggleRegion,
    toggleEquipment,
    selected,
    onRowPress,
    pairIdxs,
    togglePair,
    nameOverride,
    setNameOverride,
    filtered,
    present,
    error,
    nameForSlug,
    hasFilters,
    activeFilterCount,
    filtersOpen,
    setFiltersOpen,
    clearFilters,
    supersetTotal,
    commitAdd,
    commitSuperset,
    insertTemplate,
  } = useExercisePickerState({
    visible,
    supersetContext,
    presentSlugs,
    presentTreatment,
    eligibleSlugs,
    catalog,
    loadRecency,
    bookmarks,
    getExerciseName,
    onAddMany,
    onCreateSuperset,
    onClose,
  });

  const pickerDays = usePickerDays(visible, addDays);

  const styles = makeStyles(theme);
  const isSuperset = !singleMode && tab === 'superset';
  // PE-40 — both halves or neither: a tab whose handler is missing is a tap
  // that opens a list and throws the answer away, which is the exact defect
  // PE-30 paid for in the prescribe picker.
  const canImportWorkout =
    !singleMode && workouts !== undefined && onAddWorkout !== undefined;
  const isWorkout = canImportWorkout && tab === 'workout';
  // #85: the Programs tab, by the same both-halves rule.
  const canImportProgram =
    !singleMode && programs !== undefined && onAddProgramDay !== undefined;
  const isProgram = canImportProgram && tab === 'program';
  const tabsShown: PickerTab[] = [
    'exercises',
    'superset',
    ...(canImportWorkout ? (['workout'] as const) : []),
    ...(canImportProgram ? (['program'] as const) : []),
  ];

  // One label per treatment, resolved once instead of per row. The two say
  // different things because the two lists ARE different: the logger's set is
  // the whole workout, the composer's is only the block being edited (a
  // legitimate second block on another day is not "already there").
  const presentLabel =
    presentTreatment === 'mark-workout'
      ? t('mobile.logger.pickerAlreadyInWorkout', {
          defaultValue: 'Already in this workout',
        })
      : presentTreatment === 'mark-program'
        ? t('mobile.logger.pickerAlreadyInBlock', {
            defaultValue: 'Already in this block',
          })
        : null;

  // The superset panel and the filter rows SCROLL WITH the catalog instead
  // of sitting above it (LG-32, 2026-08-09). They used to be fixed siblings
  // of the list, and on the Superset tab the panel alone measured 968px of
  // a 1920px screen: the list — which carried no flex — was left with zero
  // height, so the only exercises still reachable were the "pair with"
  // chips, i.e. the ones already in the workout. Search and tabs stay
  // pinned: those are the tools you reach for constantly.
  //
  // Passed as an ELEMENT, never as an inline `() => (...)`. VirtualizedList
  // only reuses what `isValidElement` accepts and otherwise treats the
  // function as a component type, so a fresh identity each render would
  // remount this whole subtree on every keystroke in the search box.
  // Typing means you are browsing the catalog, so the panel's shortcuts get
  // out of the way: with the keyboard up the list is only ~350px tall and the
  // panel is ~1300, so leaving it in cost six swipes before the first match
  // appeared — measured on the headless test device, and it was still the LG-32 symptom.
  // Clear the query and the shortcuts come back.
  const searching = query.trim() !== '';
  const listHeader = (
    <>
      {isSuperset && !searching ? (
        <SupersetPanel
          context={supersetContext}
          templates={templates}
          selectedPairIdxs={pairIdxs}
          onTogglePair={togglePair}
          onJoinGroup={(g) => {
            onJoinGroup?.(g);
            onClose();
          }}
          onInsertTemplate={insertTemplate}
          onRemoveTemplate={onRemoveTemplate}
          confirm={confirm}
          nameOf={nameForSlug}
          styles={styles}
        />
      ) : null}

      {filtersOpen ? (
        <>
          <FilterChipsRow
            theme={theme}
            label={t('catalog.region')}
            options={MuscleRegion.options}
            active={regions}
            onToggle={toggleRegion}
            renderLabel={(region) => t(`catalog.region_${region}`, { defaultValue: region })}
          />
          <FilterChipsRow
            theme={theme}
            label={t('catalog.equipment')}
            options={EquipmentKind.options}
            active={equipment}
            onToggle={toggleEquipment}
            renderLabel={(kind) => t(`catalog.equipment_${kind}`, { defaultValue: kind })}
          />
        </>
      ) : null}

      {hasFilters ? (
        <Pressable accessibilityRole="button" onPress={clearFilters} style={({ pressed }) => [styles.clearFilters, pressed && styles.clearFiltersPressed]}>
          <Text style={styles.clearFiltersText}>{t('mobile.logger.pickerClearFilters')}</Text>
        </Pressable>
      ) : null}
    </>
  );

  // Still three states, not one: `filtered === null` is "catalog not loaded
  // yet" and an empty array is "loaded, nothing matches". Both give the
  // FlatList a zero-length data array, so collapsing them here would show
  // "no exercises" while the fetch is still in flight.
  const listEmpty =
    filtered === null && error === null ? (
      <View style={styles.center}>
        <ActivityIndicator color={theme.palette.ink2} />
      </View>
    ) : error !== null ? (
      <View style={styles.center}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    ) : (
      // Same centring wrapper as the spinner and the error above it: the
      // three states have to occupy the residual space identically, or the
      // empty one hugs the filters with a screen of blank under it and
      // reads as a broken layout rather than as an answer.
      <View style={styles.center}>
        <EmptyState theme={theme} title={t('mobile.logger.pickerEmpty')} />
      </View>
    );

  return (
    <>
        {/* DS-42 — the place chip left the header's right cell and moved into
            its own named band below, because choosing a gym is a SESSION-wide
            gesture and the title row reads as chrome. ⛔ It moves here too, not
            only in the swap picker: Antenore's D2b rule is that add and replace
            use the same primitive in the same slot, so changing one alone would
            create exactly the drift that rule forbids. */}
        <View style={styles.header}>
          <Pressable
            accessibilityRole="button"
            onPress={onClose}
            style={styles.cancelHit}
            hitSlop={{ top: 12, bottom: 12, left: 16, right: 8 }}
          >
            <Text style={styles.cancel}>{t('mobile.logger.cancel')}</Text>
          </Pressable>
          <Text style={styles.title} numberOfLines={1}>
            {t('mobile.logger.pickerTitle')}
          </Text>
          <View style={styles.headerSpacer} />
        </View>
        <PickerSessionBand
          theme={theme}
          label={t('swap.bandSession', { defaultValue: 'Session' })}
          gymChip={gymChip}
        />

        {!singleMode ? (
          <PickerTabBar
            styles={styles}
            t={t}
            tabs={tabsShown}
            active={tab}
            onSelect={setTab}
          />
        ) : null}

        {/* PE-40 — the Workout tab REPLACES the body, it does not sit under it:
            search, filters, the exercise list and the multi-select footer all
            answer "which exercises", and none of them is a question about which
            saved workout to import. ⛔ When `workouts` is absent this branch is
            unreachable and the tree below is byte-identical to what the Logger
            has always rendered. */}
        {isProgram && programs && onAddProgramDay ? (
          <PickerProgramList
            theme={theme}
            styles={styles}
            t={t}
            visible={visible}
            programs={programs}
            onPick={(program, sessionIdx) => {
              onAddProgramDay(program, sessionIdx);
              onClose();
            }}
          />
        ) : isWorkout && workouts && onAddWorkout ? (
          <PickerPlanList
            theme={theme}
            styles={styles}
            t={t}
            weeks={workoutImportWeeks ?? 1}
            workouts={workouts}
            onPick={(plan) => {
              onAddWorkout(plan);
              onClose();
            }}
          />
        ) : (
        <>
        {/* Pinned, never scrolls: while you search for a partner you must not
            lose sight of WHO you are pairing with. Brand tint and not a fill,
            same as the chip it replaces — it states a fact, it is not a
            control (AP-0k2). */}
        {isSuperset && supersetContext?.currentName ? (
          <View style={styles.contextStrip}>
            <Text style={styles.panelLabel}>
              {t('mobile.superset.startingWith', {
                defaultValue: 'Starting superset with',
              })}
            </Text>
            <View style={[styles.chip, styles.chipAnchor, styles.contextChip]}>
              <Text style={styles.chipText} numberOfLines={1}>
                {supersetContext.currentName}
              </Text>
            </View>
          </View>
        ) : null}

        <PickerSearchRow
          theme={theme}
          styles={styles}
          t={t}
          query={query}
          setQuery={setQuery}
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          activeFilterCount={activeFilterCount}
          resultCount={filtered ? filtered.length : null}
        />

        <FlatList
          style={styles.list}
          contentContainerStyle={styles.listContent}
          data={filtered ?? []}
          keyExtractor={(ex) => ex.slug}
          ListHeaderComponent={listHeader}
          ListEmptyComponent={listEmpty}
          renderItem={({ item }) => (
            <ExercisePickerRow
              exercise={item}
              name={getExerciseName(item.name)}
              selected={selected.has(item.slug)}
              multiSelect={!singleMode}
              bookmarked={bookmarks.isBookmarked(item.slug)}
              theme={theme}
              presentLabel={
                presentLabel !== null && present.has(item.slug) ? presentLabel : null
              }
              onPress={() => {
                if (singleMode) {
                  onPickSingle?.(item);
                  onClose();
                } else {
                  onRowPress(item);
                }
              }}
              onToggleStar={() => bookmarks.toggle(item.slug)}
              PainDot={PainDot}
              usePainRiskLabel={usePainRiskLabel}
              styles={styles}
            />
          )}
          ItemSeparatorComponent={() => <View style={[styles.separator, { backgroundColor: theme.palette.border }]} />}
          keyboardShouldPersistTaps="handled"
        />

        {!singleMode ? (
          <PickerFooter
            theme={theme}
            styles={styles}
            t={t}
            variant={isSuperset ? 'superset' : 'add'}
            nameOverride={nameOverride}
            setNameOverride={setNameOverride}
            supersetTotal={supersetTotal}
            commitSuperset={commitSuperset}
            selectedCount={selected.size}
            commitAdd={() => commitAdd(pickerDays.chosen())}
            days={addDays}
            selectedDays={pickerDays.days}
            onToggleDay={pickerDays.toggleDay}
          />
        ) : null}
        </>
        )}
    </>
  );
}
